import {
    Colors,
    CollapsibleSection,
    DATE_FORMATS,
    formatDate,
    Heading,
    Text,
} from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import type { ObjectiveWindow } from '@/types/objectives';

import { EmployeeObjectivesOverview } from '@/components/objectives/EmployeeObjectivesOverview';
import { useAmelioTranslations } from '@/hooks';
import { useEmployeeObjectives } from '@/hooks/objectives/useEmployeeObjectives';
import i18n from '@/plugins/i18n';

/** The evaluation period as the manager sees it above the survey: its days, and its name if it has one. */
export interface EvaluationObjectivesPeriod {
    /** Calendar day, `'YYYY-MM-DD'`. */
    from: string;
    /** Calendar day, `'YYYY-MM-DD'`. */
    to: string;
    /** The predefined period's name; absent for a custom range, which is then named by its dates. */
    name?: string;
}

interface EvaluationObjectivesProps {
    employeeId: string;
    period: EvaluationObjectivesPeriod;
}

/**
 * The objectives themselves, mounted only once the section is opened: a closed section asks the
 * server nothing, and by the time a manager opens it the evaluation period has settled.
 */
const EvaluationObjectives = ({ employeeId, period: { from, to } }: EvaluationObjectivesProps) => {
    const { t } = useAmelioTranslations();

    // The server keeps every objective whose own dates overlap the window, so an objective that
    // straddles one end of the evaluation period still counts in it.
    const objectiveWindow = useMemo<ObjectiveWindow>(
        () => ({ employeeId, from, to }),
        [employeeId, from, to],
    );
    const { objectives, isLoadingObjectives, areObjectivesInError, areObjectivesKnown, summary } =
        useEmployeeObjectives(objectiveWindow);

    if (areObjectivesInError) {
        return (
            <Text color={Colors.ERROR}>
                {t('managerAssessmentEvaluation.employeeObjectives.loadError')}
            </Text>
        );
    }

    return (
        <EmployeeObjectivesOverview
            objectives={objectives}
            summary={summary}
            isSummaryKnown={areObjectivesKnown}
            isLoadingObjectives={isLoadingObjectives}
            windowKey={`${from}/${to}`}
            // Read-only on the evaluation (Maxime, 2026-09-14): a tile opens its objective, and the
            // evaluation offers no way to edit or delete one.
            isReadOnly
            // The block is one section among several here, inside a panel the manager scrolls
            // through while writing: it reads at the tighter scale so the evaluation itself stays
            // in view. This line is the whole difference with the profile's Objectives tab.
            density={'compact'}
        />
    );
};

interface EmployeeObjectivesSectionProps {
    employeeId: string;
    employeeFirstName: string;
    /** `null` while the evaluation has no complete and valid period to read the objectives in. */
    period: EvaluationObjectivesPeriod | null;
    className?: string;
    dataTestId?: string;
}

/**
 * The assessed employee's objectives on the manager's evaluation page, above the meeting topics:
 * a section closed by default, headed by the evaluation period, that opens onto the objectives
 * overlapping that period -- the same progression, tiles and list as the profile's Objectives tab,
 * each objective a link to its detail. What the manager reviews comes before what they plan to
 * discuss.
 */
export const EmployeeObjectivesSection = ({
    employeeId,
    employeeFirstName,
    period,
    className,
    dataTestId = 'employee-objectives-section',
}: EmployeeObjectivesSectionProps) => {
    const { t } = useAmelioTranslations();
    const [isOpen, setIsOpen] = useState(false);

    const formatDay = (date: string) =>
        formatDate({ date, format: DATE_FORMATS.DISPLAY_DATE, language: i18n.language });
    const describePeriod = ({ from, to, name }: EvaluationObjectivesPeriod) =>
        name ??
        t('managerAssessmentEvaluation.employeeObjectives.customPeriod', {
            from: formatDay(from),
            to: formatDay(to),
        });

    return (
        <CollapsibleSection
            isOpen={isOpen}
            onToggle={() => setIsOpen((current) => !current)}
            variant={'glass'}
            className={className}
            dataTestId={dataTestId}
            header={
                <span className={'flex flex-col gap-1 text-left'}>
                    {/* The whole header is the library's toggle button, which may only hold
                        phrasing content: the title keeps the heading look on a `span`. */}
                    <Heading
                        level={4}
                        as={'span'}
                        color={Colors.NAVY}
                    >
                        {t('managerAssessmentEvaluation.employeeObjectives.title', {
                            name: employeeFirstName,
                        })}
                    </Heading>
                    <Text
                        as={'span'}
                        size={'small'}
                        color={Colors.BASE_500}
                    >
                        {period
                            ? t('managerAssessmentEvaluation.employeeObjectives.periodSummary', {
                                  period: describePeriod(period),
                              })
                            : t('managerAssessmentEvaluation.employeeObjectives.periodMissing')}
                    </Text>
                </span>
            }
        >
            {/* The same side padding as the library's glass header, so title and content align. */}
            <div className={'flex flex-col px-10 pt-6 pb-8'}>
                {period ? (
                    <EvaluationObjectives
                        employeeId={employeeId}
                        period={period}
                    />
                ) : (
                    <Text color={Colors.BASE_500}>
                        {t('managerAssessmentEvaluation.employeeObjectives.periodRequired', {
                            name: employeeFirstName,
                        })}
                    </Text>
                )}
            </div>
        </CollapsibleSection>
    );
};
